import { Chip, Stack } from "@mui/material";
import type { Control } from "react-hook-form";
import { useWatch } from "react-hook-form";
import { FormAccordionSection } from "Components/Shared/FormAccordionSection";
import { FormInput } from "Components/Shared/Inputs/Form/FormInput";
import { PhoneInput } from "Components/Shared/Inputs/Form/PhoneInput";
import type { FacilityFormModel } from "Components/Shared/Merchant/Facilities/schema";
import type { ContactSource } from "Components/Shared/Merchant/Facilities/constants";
import type { PhoneNumber } from "Infrastructure/Api/Api";
import { Resources, useResource } from "Translations/Resources";
import { phoneToE164 } from "Utils/PhoneUtils";

const RESOURCES = Resources.Components.Merchant.Facilities.Form;

const isSourceMatch = (
  source: ContactSource,
  responsiblePerson: string,
  phone: PhoneNumber | undefined,
  email: string,
): boolean =>
  source.responsiblePerson === responsiblePerson &&
  phoneToE164(source.phone) === phoneToE164(phone) &&
  (source.email ?? "") === email;

type Props = {
  control: Control<FacilityFormModel>;
  contactSources: Array<ContactSource>;
  onContactSourceSelect: (source: ContactSource) => void;
  isExpanded: boolean;
  onToggle: () => void;
  onNext: () => void;
  isComplete: boolean | undefined;
  hasErrors: boolean;
};

export const ContactSection = ({
  control,
  contactSources,
  onContactSourceSelect,
  isExpanded,
  onToggle,
  onNext,
  isComplete,
  hasErrors,
}: Props) => {
  const { t } = useResource();

  const [currentResponsiblePerson, currentPhone, currentEmail] = useWatch({
    control,
    name: ["responsiblePerson", "phone", "email"],
  });

  const matchedIndex = contactSources.findIndex((source) =>
    isSourceMatch(
      source,
      currentResponsiblePerson ?? "",
      currentPhone,
      currentEmail ?? "",
    ),
  );

  const hasContactSources = contactSources.length > 0;

  return (
    <FormAccordionSection
      legend={t(RESOURCES.Legend.Contact)}
      isExpanded={isExpanded}
      onToggle={onToggle}
      onNext={onNext}
      isComplete={isComplete}
      hasErrors={hasErrors}
    >
      {hasContactSources && (
        <Stack gap={1} mb="20px">
          <span>{t(RESOURCES.PrefillContact)}</span>
          <Stack direction="row" gap={1} flexWrap="wrap">
            {contactSources.map((source, index) => (
              <Chip
                key={`${source.responsiblePerson} + ${source.email}`}
                label={source.responsiblePerson || t(RESOURCES.NoName)}
                variant={matchedIndex === index ? "filled" : "outlined"}
                color={matchedIndex === index ? "primary" : "default"}
                onClick={() => onContactSourceSelect(source)}
              />
            ))}
          </Stack>
        </Stack>
      )}

      <FormInput
        control={control}
        name="responsiblePerson"
        label={t(RESOURCES.Fields.ResponsiblePerson)}
      />
      <PhoneInput
        control={control}
        name="phone"
        label={t(RESOURCES.Fields.Phone)}
      />
      <FormInput
        control={control}
        name="email"
        label={t(RESOURCES.Fields.Email)}
      />
    </FormAccordionSection>
  );
};
